import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { Button } from "@commonsku/styles";
import { useReportContext } from "../report/report_context";
import { EReportTypes } from "../report/routes/report_types";
import { useHasCapabilities } from "../../hooks";
import { oauth } from "../../utils";
import ExportToAccountingPopup, { AccountingExportReport, EntityType, exportToAccounting } from "./ExportToAccountingPopup";

const REPORT_TYPE_TO_EXPORT_PARAM_MAP = {
  [EReportTypes.FinanceReceiveBills]: {
    entityType: "PURCHASE-ORDER",
    rowId: "purchase_order_id",
  },
  [EReportTypes.FinanceExportedBills]: {
    entityType: "BILL",
    rowId: "bill_id",
  },
  [EReportTypes.FinanceExportedInvoices]: {
    entityType: "INVOICE",
    rowId: "order_id",
  },
};

const StyledButton = styled(Button)`
  white-space: pre;
`;

export default function ExportAllButton() {
  const {
    reportConfig: { type, endpoint },
    getRawQuery,
    firstLoaded,
    loading
  } = useReportContext();
  const [executing, setExecuting] = useState(false);
  const [ids, setIds] = useState<string[]>([]);
  const [accountingExportReport, setAccountingExportReport] = useState<AccountingExportReport | null | false>(false);
  const hasExportReport = useHasCapabilities("EXPORT-REPORT");
  const { entityType, rowId } = REPORT_TYPE_TO_EXPORT_PARAM_MAP[type] ?? {};

  const handleClick = useCallback(async () => {
    try {
      setExecuting(true);
      setIds([]);
      setAccountingExportReport(null);
      const query = getRawQuery();
      const response = await oauth("GET", endpoint, query);
      const ids = response.json.report.map(row => row[rowId]);
      setIds(ids);
      await exportToAccounting(entityType, "CSV", ids, true);
    } catch (e) {
      toast.error("Unable to export");
    } finally {
      setExecuting(false);
      setAccountingExportReport(false);
      setIds([]);
    }
  }, [endpoint, getRawQuery, type, entityType, rowId]);

  const handleClose = useCallback(
    () => {
      setAccountingExportReport(false);
      setExecuting(false);
    },
    []
  );

  const disabled = !firstLoaded || loading || executing;

  if (!hasExportReport || !entityType) {
    return null;
  }

  return (
    <StyledButton secondary onClick={handleClick} disabled={disabled}>
        {false !== accountingExportReport && <ExportToAccountingPopup
          accountingSoftware="CSV"
          entityType={entityType}
          ids={ids}
          onClose={handleClose}
          report={accountingExportReport}
        />}
      Export All To CSV
    </StyledButton>
  );
};
